import React, { useMemo, useState } from "react";
import { MapPin, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { HungaryCountyMap } from "@/components/HungaryCountyMap";
import { HUNGARY_COUNTIES } from "@/lib/hungaryCountyPaths";
import { buildCountyCounts } from "@/lib/carCountyCounts";

/** „Hol keresel?” — megye szűrő térképpel. */
export function CountyFilter({ cars, selected = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selected);

  const counts = useMemo(() => buildCountyCounts(cars), [cars]);

  const openPicker = () => {
    setDraft(selected);
    setOpen(true);
  };

  const toggleDraft = (county) => {
    setDraft((prev) => (prev.includes(county) ? prev.filter((c) => c !== county) : [...prev, county]));
  };

  const apply = () => {
    onChange?.(draft);
    setOpen(false);
  };

  const clearAll = () => {
    setDraft([]);
    onChange?.([]);
    setOpen(false);
  };

  const label =
    selected.length === 0
      ? "Hol keresel?"
      : selected.length === 1
        ? selected[0]
        : `${selected.length} megye`;

  const draftSummary = draft.length
    ? draft.map((c) => `${c} (${counts[c] || 0})`).join(", ")
    : "Összes megye";

  return (
    <>
      <button
        type="button"
        data-testid="filter-county-trigger"
        onClick={openPicker}
        className={`inline-flex items-center gap-2 h-11 px-4 rounded-xl border text-sm transition-all w-full md:w-auto justify-between md:justify-start ${
          selected.length
            ? "border-electric/50 bg-electric/10 text-white"
            : "border-white/10 bg-white/[0.03] text-slate-200 hover:border-white/20"
        }`}
      >
        <span className="inline-flex items-center gap-2 min-w-0">
          <MapPin className="w-4 h-4 text-electric shrink-0" strokeWidth={1.5} />
          <span className="truncate">{label}</span>
        </span>
        {selected.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
                clearAll();
              }
            }}
            className="p-0.5 rounded-full hover:bg-white/10 shrink-0"
            aria-label="Megye szűrő törlése"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-anthracite-800 border-white/10 text-white p-0 overflow-hidden light:bg-white light:text-slate-900 light:border-slate-200">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="font-heading text-xl font-light">Hol keresel?</DialogTitle>
            <DialogDescription className="text-slate-400 light:text-slate-600">
              Kattints egy vagy több megyére. A szürke megyékben jelenleg nincs autó a kínálatban.
            </DialogDescription>
          </DialogHeader>

          <div className="px-4 pb-2">
            <HungaryCountyMap selected={draft} onToggle={toggleDraft} counts={counts} />
          </div>

          <div className="px-6 py-2 text-xs text-slate-400 light:text-slate-600 min-h-[2rem]" data-testid="county-filter-summary">
            {draftSummary}
          </div>

          <div className="flex flex-wrap gap-2 p-6 pt-3 border-t border-white/10 light:border-slate-200">
            <button
              type="button"
              data-testid="county-filter-apply"
              onClick={apply}
              className="flex-1 min-w-[120px] py-2.5 rounded-full bg-electric hover:bg-electric-light text-white text-sm font-medium transition-all"
            >
              Alkalmaz
            </button>
            <button
              type="button"
              data-testid="county-filter-clear"
              onClick={() => setDraft([])}
              className="px-5 py-2.5 rounded-full border border-white/15 text-sm text-slate-300 hover:text-white transition-colors light:border-slate-300 light:text-slate-600"
            >
              Kijelölés törlése
            </button>
            <button
              type="button"
              onClick={() => setDraft([...HUNGARY_COUNTIES.filter((c) => (counts[c] || 0) > 0)])}
              className="px-5 py-2.5 rounded-full border border-white/15 text-sm text-slate-300 hover:text-white transition-colors light:border-slate-300 light:text-slate-600"
            >
              Összes elérhető
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
